import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDiscordSDK } from '../hooks/useDiscordSDK';
import Leaderboard from '../components/Leaderboard';
import type { Game } from '@tinymint/shared';

function Play() {
  const { gameId } = useParams<{ gameId: string }>();
  const [game, setGame] = useState<Game | null>(null);
  const [loading, setLoading] = useState(true);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const { serverId, user } = useDiscordSDK();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (gameId) {
      fetchGame();
    }
  }, [gameId]);

  // Listen for messages from the game iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === 'TINYMINT_SHOW_LEADERBOARD') {
        setShowLeaderboard(true);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const fetchGame = async () => {
    try {
      const response = await fetch(`/.proxy/api/games/${gameId}`);
      if (response.ok) {
        const data = await response.json();
        setGame(data);
      }
    } catch (error) {
      console.error('Failed to fetch game:', error);
    } finally {
      setLoading(false);
    }
  };

  // Send context to game iframe when it loads
  const handleIframeLoad = () => {
    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage({
        type: 'TINYMINT_CONTEXT',
        payload: {
          gameId,
          serverId,
          userId: user?.id,
          username: user?.global_name || user?.username,
        },
      }, '*');
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="loading-spinner" />
      </div>
    );
  }

  if (!game) {
    return (
      <div className="loading">
        <p>Game not found</p>
        <button className="btn" style={{ marginTop: '1rem' }} onClick={() => navigate('/')}>
          Back to Gallery
        </button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', gap: '1rem', height: 'calc(100vh - 140px)' }}>
      {/* Game Player */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <div style={{ marginBottom: '0.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button className="nav-btn" onClick={() => navigate('/')}>
            ← Back
          </button>
          <span style={{ fontWeight: 600 }}>{game.prompt}</span>
          <button
            className="nav-btn"
            onClick={() => setShowLeaderboard(!showLeaderboard)}
          >
            🏆 Leaderboard
          </button>
        </div>
        <div className="game-player-frame" style={{ flex: 1 }}>
          <iframe
            ref={iframeRef}
            src={`/.proxy/api/games/${gameId}/play`}
            title={game.prompt}
            onLoad={handleIframeLoad}
            allow="gamepad"
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
              imageRendering: 'pixelated',
            }}
          />
        </div>
        <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
          Controls: Arrow keys to move, X/Z for buttons
        </div>
      </div>

      {/* Leaderboard Sidebar */}
      {showLeaderboard && (
        <div style={{ width: '280px' }}>
          <Leaderboard
            gameId={gameId!}
            serverId={serverId!}
            currentUserId={user?.id}
            onClose={() => setShowLeaderboard(false)}
          />
        </div>
      )}
    </div>
  );
}

export default Play;
